import { FDDM_TAG, Tag } from '@modules/floorplanDesign/constants';
import { theme } from '@modules/common/theme';
import { HideFloorplanElementsOptions } from '../types';

export const MIN_ZOOM = 0;
export const MAX_ZOOM = 200;

export const HIGHWAY_ARROW_ID = 'arrow';
export const HIGHWAY_ARROW_SIZE = 200;

export const MIN_BUBBLE_RADIUS = 30;
export const MAX_BUBBLE_RADIUS = 130;

export const TRACK_PIECE_COLOR = `${theme.palette.splines}`;
export const TRACK_PIECE_SELECTED_COLOR = theme.palette.splines;

export const GATE_MAPPING_ARROW_TRIM = 0.025;
export const GATE_MAPPING_COLOR = `${theme.palette.others.medium}80`;
export const GATE_MAPPING_SELECTED_COLOR = theme.palette.status.scheduled;

export const POI_CONNECTION_SCALE = 1000;
export const POI_CONNECTION_OFFSET = 100;
export const MIN_POI_CONNECTION_LENGTH = 1;
export const VEHICLE_LAYOUT_POI_CONNECTION_COLOR = `${theme.palette.primary.main}80`;

/** Radius in meters of the circles shown on an area's connection points while it is transformed. */
export const AREA_CONNECTION_POINT_RADIUS = 0.15;
export const AREA_CONNECTION_POINT_COLOR = theme.palette.primary.main;

export const AREA_MINIMUM_SIZE = 1000;
export const OBSTACLE_MINIMUM_SIZE = 50;
export const WALL_MINIMUM_LENGTH = 50;
export const ANGLED_HIGHWAY_MINIMUM_LENGTH = 100;

export const BUBBLE_LINE_WIDTH = 20;
export const BUBBLE_RADIUS = 90;
export const FORKLIFT_SIZE = 400;
export const RACK_SIZE = 400;

export const TRACK_PIECE_SIZE = 20;

export const FLOW_STROKE_WIDTH = 20;
export const FLOW_OFFSET_TO_ATTACH_POINT = 250;

export const DIST_CON_LINE_STROKE_WIDTH = 2;

export const MAX_LINE_PATH_DIVISIONS = 23;

export const CONTEXT_MENU_CONTROL_POINT_DETECTION_RADIUS = 100;

export const JIT_POINT_SIZE = 0.5;

/**
 * Tolerance in meters used to determine whether two positioned elements
 * (e.g. POIs, stations) share the same 2D center and should be deduplicated
 * during rendering.
 */
export const ELEMENT_CENTER_EPSILON = 1e-3;

export const Z_INDEX_MAP: Record<
  Exclude<
    Tag,
    | typeof FDDM_TAG.TEMPLATE
    | typeof FDDM_TAG.GROUP
    | typeof FDDM_TAG.FLOW_STOP
    | typeof FDDM_TAG.A_TO_B_CONNECTION
    | typeof FDDM_TAG.B_TO_A_CONNECTION
    | typeof FDDM_TAG.INTER_AREA_CONNECTION
    | typeof FDDM_TAG.MODEL
    | typeof FDDM_TAG.LAYER_ANGLED_HIGHWAYS
    | typeof FDDM_TAG.LAYER_AREAS
    | typeof FDDM_TAG.LAYER_AREA_NAMES
    | typeof FDDM_TAG.LAYER_CHARGING
    | typeof FDDM_TAG.LAYER_CONNECTIONS
    | typeof FDDM_TAG.LAYER_CONNECTION_BUBBLES
    | typeof FDDM_TAG.LAYER_DELIVERY
    | typeof FDDM_TAG.LAYER_EXTRACTION
    | typeof FDDM_TAG.LAYER_FLOWS
    | typeof FDDM_TAG.LAYER_HIGHWAYS
    | typeof FDDM_TAG.LAYER_HIGHWAY_NAMES
    | typeof FDDM_TAG.LAYER_INSERTION
    | typeof FDDM_TAG.LAYER_INTAKE
    | typeof FDDM_TAG.LAYER_INTERIOR
    | typeof FDDM_TAG.LAYER_MANUAL
    | typeof FDDM_TAG.LAYER_NAMES
    | typeof FDDM_TAG.LAYER_OBSTACLES
    | typeof FDDM_TAG.LAYER_OBSTACLE_NAMES
    | typeof FDDM_TAG.LAYER_PARKING
    | typeof FDDM_TAG.LAYER_PROCESS
    | typeof FDDM_TAG.LAYER_REFERENCE
    | typeof FDDM_TAG.LAYER_ROADS
    | typeof FDDM_TAG.LAYER_STORAGE
    | typeof FDDM_TAG.LAYER_WALLS
    | typeof FDDM_TAG.LAYER_FLOORPLAN
    | typeof FDDM_TAG.LAYER_LOGICAL_SPACES
    | typeof FDDM_TAG.LAYER_GROUPS
  >,
  number
> = {
  [FDDM_TAG.REFERENCE_IMAGE]: -100,

  [FDDM_TAG.ORIGIN]: 0,
  [FDDM_TAG.SINGULAR_STATION_AREA]: 0,
  [FDDM_TAG.STATIONARY_AREA]: 0,

  [FDDM_TAG.RACKING_AREA]: 0,
  [FDDM_TAG.HIGHWAY_AREA]: 0,
  [FDDM_TAG.ANGLED_HIGHWAY_AREA]: 0,
  [FDDM_TAG.MANUAL_AREA]: 0,
  [FDDM_TAG.GATE_MAPPING]: 1,

  [FDDM_TAG.SWEPT_PATH]: 3,
  [FDDM_TAG.OBSTACLE]: 4,
  [FDDM_TAG.WALL]: 4,
  [FDDM_TAG.POINT_OF_INTEREST]: 5,
  [FDDM_TAG.STATION]: 10,
  [FDDM_TAG.JIT_POINT]: 10,
  [FDDM_TAG.AREA_CONNECTION_POINT]: 13,
  [FDDM_TAG.FORKLIFT]: 10,

  [FDDM_TAG.TRACK_PIECE]: 10,
  [FDDM_TAG.BUBBLE]: 12,
  [FDDM_TAG.BUBBLE_ARROW]: 13,
  [FDDM_TAG.BUBBLE_DIVIDER]: 13,
  [FDDM_TAG.CROSSING]: 13,
  [FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION]: 15,
  [FDDM_TAG.DISTANT_CONNECTION]: 12,
  [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD]: 10,
  [FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD_TEXT]: 11,
  [FDDM_TAG.POSE]: 11,
  [FDDM_TAG.HIGHWAY_ARROW]: 12, // icon
  [FDDM_TAG.RACK]: 12, // icon
  [FDDM_TAG.FLOW]: 12,
  [FDDM_TAG.FLOW_LOADS_PER_HOUR]: 13,
  [FDDM_TAG.LOGICAL_SPACE]: 13,
  [FDDM_TAG.REFERENCE_SCALING_LINE]: 20,
  [FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A]: 20,
  [FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B]: 20,
};

export const HIDE_TEMPLATE_RELATED_ELEMENTS_OPTIONS: HideFloorplanElementsOptions = {
  hideStations: true,
  hidePois: true,
  hidePoses: true,
  hideGateMappings: true,
  hideConnections: true,
  hideFlowStops: true,
  hideFlows: true,
};
